import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, Timer } from "lucide-react";

/**
 * Renders a fixed banner across every page when the user is in an active
 * kiosk session (localStorage `kiosk_session === "1"`). Tracks idle time via
 * global pointer/touch/key listeners and auto-logs the user out after 90s of
 * inactivity, clearing `icard_token` and returning to /app/kiosk.
 *
 * Mounted once at the root of <Router> so it survives navigation between
 * existing pages (wallet, vendors, hostels…). Any interaction anywhere on the
 * page resets the timer.
 */
const IDLE_LIMIT_SEC = 90;       // 90 s per user choice 2.b
const TICK_MS = 1000;

export default function KioskSessionGuard() {
  const nav = useNavigate();
  const loc = useLocation();
  const [active, setActive] = useState(() => localStorage.getItem("kiosk_session") === "1");
  const [secondsLeft, setSecondsLeft] = useState(IDLE_LIMIT_SEC);
  const lastActivityRef = useRef(Date.now());
  const tickRef = useRef(null);

  // Listen for cross-tab changes to the kiosk_session flag.
  useEffect(() => {
    const sync = () => setActive(localStorage.getItem("kiosk_session") === "1");
    window.addEventListener("storage", sync);
    // Also re-check on route changes (Kiosk.jsx sets the flag without firing 'storage').
    sync();
    return () => window.removeEventListener("storage", sync);
  }, [loc.pathname]);

  const logout = useCallback((reason = "manual") => {
    localStorage.removeItem("icard_token");
    localStorage.removeItem("icard_user");
    localStorage.removeItem("kiosk_session");
    localStorage.setItem("kiosk_logout_reason", reason);
    setActive(false);
    nav("/app/kiosk", { replace: true });
  }, [nav]);

  // Reset idle timer on ANY interaction (keyboard / pointer / touch).
  useEffect(() => {
    if (!active) return;
    const onActivity = () => {
      lastActivityRef.current = Date.now();
      setSecondsLeft(IDLE_LIMIT_SEC);
    };
    const events = ["pointerdown", "touchstart", "keydown", "wheel"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [active]);

  // Countdown tick — when it hits 0, auto-logout.
  useEffect(() => {
    if (!active) return;
    tickRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.max(0, IDLE_LIMIT_SEC - elapsed);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(tickRef.current);
        logout("idle_timeout");
      }
    }, TICK_MS);
    return () => clearInterval(tickRef.current);
  }, [active, logout]);

  if (!active) return null;

  // Hide the bar on the kiosk landing & PIN pages themselves (the kiosk own UI
  // owns its layout). Show on every OTHER app route used inside the session.
  if (loc.pathname === "/kiosk" || loc.pathname === "/app/kiosk") {
    return null;
  }

  const warning = secondsLeft <= 15;
  return (
    <div
      data-testid="kiosk-session-bar"
      className={`fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 px-4 py-2 text-white font-semibold text-xs sm:text-sm shadow-lg ${warning ? "bg-[#EF4444]" : "bg-[#7C3AED]"}`}
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.5rem)" }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Timer size={14} className="flex-shrink-0" />
        <span className="truncate">Kiosk session · auto logout in <span className="font-mono font-black">{secondsLeft}s</span></span>
      </div>
      <button
        data-testid="kiosk-session-logout"
        onClick={() => logout("manual")}
        className="flex items-center gap-1 px-3 py-1.5 bg-black/30 hover:bg-black/50 rounded-lg text-xs font-bold flex-shrink-0"
      >
        <LogOut size={12} /> Logout
      </button>
    </div>
  );
}
